import { confirmDialog, notify } from '../utils/notify';
// ============================================================================
// DEDUCTIONS PAGE - frontend/src/pages/Deductions.js
// ============================================================================

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { deductionAPI } from '../services/api';
import { Pencil, Plus, Power, PowerOff, Trash2 } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import './Deductions.css';

const isPercentage = (deduction) => deduction?.calculation_type === 'percentage';

const Deductions = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState(null);
  // The catalog payroll reads (deductions, type 'deduction'), in SRD or USD.
  // Payroll takes the default amount only for employees paid in its currency.
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    currency: 'SRD',
    default_amount: '',
    description: '',
    is_active: true
  });

  const canManage = ['super_admin', 'hr_manager', 'finance_manager'].includes(user?.role);

  useEffect(() => {
    loadDeductions();
  }, []);

  const loadDeductions = async () => {
    try {
      setLoading(true);
      const response = await deductionAPI.getAll();
      const data = response.data;
      if (data.success) {
        setDeductions(data.deductions);
      }
    } catch (error) {
      console.error('Error loading deductions:', error);
      notify(t('Error loading deductions'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase()
      };
      // A percentage deduction has no default amount (payroll does not apply
      // the percentage): each employee is given their own amount.
      if (isPercentage(editingDeduction)) delete payload.default_amount;
      const response = editingDeduction
        ? await deductionAPI.update(editingDeduction.id, payload)
        : await deductionAPI.create(payload);
      const data = response.data;

      if (data.success) {
        notify(editingDeduction ? t('Deduction updated!') : t('Deduction created!'), 'success');
        setShowModal(false);
        resetForm();
        loadDeductions();
      } else {
        notify(t('Error: {message}', { message: data.message }), 'error');
      }
    } catch (error) {
      console.error('Error saving deduction:', error);
      notify(error.response?.data?.message || t('Error saving deduction'), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog(t('Delete this deduction? This cannot be undone.'))) return;

    try {
      const response = await deductionAPI.delete(id);
      const data = response.data;

      if (data.success) {
        notify(t('Deduction deleted successfully'), 'success');
        loadDeductions();
      } else {
        notify(t('Error: {message}', { message: data.message }), 'error');
      }
    } catch (error) {
      console.error('Error deleting deduction:', error);
      notify(error.response?.data?.message || t('Error deleting deduction'), 'error');
    }
  };

  const handleToggleStatus = async (deduction) => {
    try {
      const response = await deductionAPI.update(deduction.id, {
        is_active: !deduction.is_active
      });
      const data = response.data;

      if (data.success) {
        loadDeductions();
      } else {
        notify(t('Error: {message}', { message: data.message }), 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      notify(t('Error updating status'), 'error');
    }
  };

  const openEditModal = (deduction) => {
    setEditingDeduction(deduction);
    setFormData({
      name: deduction.name,
      code: deduction.code,
      currency: deduction.currency || 'SRD',
      default_amount: deduction.default_amount || '',
      description: deduction.description || '',
      is_active: deduction.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      currency: 'SRD',
      default_amount: '',
      description: '',
      is_active: true
    });
    setEditingDeduction(null);
  };

  return (
    <div className="deductions-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Deductions Management')}</h1>
          <p className="page-sub">{t('Configure employee deductions')}</p>
        </div>
        {canManage && (
          <button 
            onClick={() => { resetForm(); setShowModal(true); }} 
            className="btn btn--primary"
          >
            <Plus aria-hidden="true" /> {t('Add Deduction')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="empty">{t('Loading deductions...')}</div>
      ) : (
        <div className="ded-grid">
          {deductions.map(deduction => (
            <div 
              key={deduction.id} 
              className={`ded-card${!deduction.is_active ? ' is-inactive' : ''}`}
            >
              <div className="ded-card__head">
                <div className="ded-card__who">
                  <h3>{deduction.name}</h3>
                  <span className="ded-card__code">{deduction.code}</span>
                </div>
                <StatusBadge status={deduction.is_active ? 'active' : 'inactive'}>
                  {deduction.is_active ? t('Active') : t('Inactive')}
                </StatusBadge>
              </div>

              <div className="ded-card__details">
                {isPercentage(deduction) ? (
                  <div className="ded-card__detail">
                    <span className="ded-card__label">{t('Percentage')}:</span>
                    <span className="ded-card__value">{parseFloat(deduction.percentage || 0)}% · {t('each employee needs their own amount')}</span>
                  </div>
                ) : (
                  <div className="ded-card__detail">
                    <span className="ded-card__label">{t('Default Amount')}:</span>
                    <span className="ded-card__value num">
                      {deduction.currency} {parseFloat(deduction.default_amount || 0).toLocaleString(locale, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                )}
                {deduction.description && (
                  <div className="ded-card__detail">
                    <span className="ded-card__label">{t('Description')}:</span>
                    <span className="ded-card__value">{deduction.description}</span>
                  </div>
                )}
              </div>

              {canManage && (
                <div className="ded-card__actions">
                  <button 
                    onClick={() => openEditModal(deduction)}
                    className="btn btn--secondary"
                    title={t('Edit')}
                  >
                    <Pencil aria-hidden="true" /> {t('Edit')}
                  </button>
                  <button 
                    onClick={() => handleToggleStatus(deduction)}
                    className="btn btn--secondary"
                    title={deduction.is_active ? t('Deactivate') : t('Activate')}
                  >
                    {deduction.is_active ? <PowerOff aria-hidden="true" /> : <Power aria-hidden="true" />} {deduction.is_active ? t('Deactivate') : t('Activate')}
                  </button>
                  <button 
                    onClick={() => handleDelete(deduction.id)}
                    className="btn btn--secondary ded-delete"
                    title={t('Delete')}
                  >
                    <Trash2 aria-hidden="true" /> {t('Delete')}
                  </button>
                </div>
              )}
            </div>
          ))}

          {deductions.length === 0 && (
            <div className="empty">
              <p>{t('No deductions configured')}</p>
              {canManage && (
                <button onClick={() => setShowModal(true)} className="btn btn--primary">
                  {t('Add First Deduction')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <Modal open title={editingDeduction ? t('Edit Deduction') : t('Create Deduction')} onClose={() => { setShowModal(false); resetForm(); }} size="md">

            <div className="ded-dialog">
              <form onSubmit={handleSubmit}>
                <div className="ded-field">
                  <label className="field-label">{t('Deduction Name')} *</label>
                  <input className="input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={t('e.g., Health Insurance')}
                    required
                  />
                </div>

                <div className="ded-field">
                  <label className="field-label">{t('Deduction Code')} *</label>
                  <input className="input"
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder={t('e.g., HEALTH_INS')}
                    required
                  />
                  <small className="field-hint">{t('Unique identifier (will be converted to uppercase)')}</small>
                </div>

                <div className="ded-field">
                  <label className="field-label">{t('Currency')} *</label>
                  <select className="select"
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                    required
                  >
                    <option value="SRD">SRD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>

                {isPercentage(editingDeduction) ? (
                  <div className="ded-field">
                    <label className="field-label">{t('Percentage')}</label>
                    <p>{parseFloat(editingDeduction.percentage || 0)}% · {t('each employee needs their own amount')}</p>
                  </div>
                ) : (
                  <div className="ded-field">
                    <label className="field-label">{t('Default Amount ({currency})', { currency: formData.currency })}</label>
                    <input className="input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.default_amount}
                      onChange={(e) => setFormData({...formData, default_amount: e.target.value})}
                      placeholder="500.00"
                    />
                    <small className="field-hint">{t('Payroll takes it when the employee has no amount of their own and is paid in this currency')}</small>
                  </div>
                )}

                <div className="ded-field">
                  <label className="field-label">{t('Description')}</label>
                  <textarea className="textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={t('Brief description of this deduction')}
                    rows="3"
                  />
                </div>

                <div className="ded-field">
                  <label className="ded-check">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <span>{t('Active')}</span>
                  </label>
                </div>

                <div className="ded-form-actions">
                  <button 
                    type="button" 
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="btn btn--secondary"
                  >
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary">
                    {editingDeduction ? t('Update Deduction') : t('Create Deduction')}
                  </button>
                </div>
              </form>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default Deductions;
